<template>
    <div class="d-flex flex-column gap-3 mt-3">
        <div class="col-12">
            <h1 class="text-3xl ps-2">
                {{ $t('Økonomi') }}
            </h1>
        </div>

        <div class="text-center col-12" v-show="isLoading">
            <span class="spinner-border" role="status"></span>
        </div>

        <div class="text-center col-12" v-show="hasError">
            <div class="alert alert-danger">{{ error }}</div>
        </div>

        <div v-if="!isLoading && hasPositiveBalance">
            <PositiveBalanceCard :balance="totalBalanceAmount" :formattedBalance="totalBalanceAmountFormatted"></PositiveBalanceCard>
        </div>

        <div v-for="invoice in invoices" v-if="!isLoading && !hasError" class="row">
            <PayableInvoice :invoice="invoice" :paymentMethod="paymentMethod" :isApp="isApp" :hashedKey="hashedKey"></PayableInvoice>
        </div>

        <div v-if="!isLoading && !hasError && notInvoicedLines.length">
            <div class="d-flex flex-column gap-3">
                <div class="hr-wrapper" v-if="invoices.length > 0 && notInvoicedLines.length > 0">
                    <hr>
                </div>
                <div class="card">
                    <div class="row">
                        <div class="col-2">
                            <div class="card-body text-elev-blue">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="card-icon">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12H12m-8.25 5.25h16.5" />
                                </svg>
                            </div>
                        </div>
                        <div class="col-10">
                            <div class="card-body pb-0">
                                <div class="row">
                                    <div class="col-12">
                                        <h5 class="card-title text-xs">{{ $t('Ufakturerte varelinjer') }}</h5>
                                        <p class="text-2xl mb-0"><strong>{{ notInvoicedAmountFormatted }}</strong></p>
                                    </div>
                                </div>
                            </div>

                            <div v-if="showPaymentButton">
                                <div>
                                    <div class="hr-wrapper">
                                        <hr>
                                    </div>

                                    <div class="card-body pt-1">
                                        <div class="row">
                                            <div class="col-sm-6 col-12 pe-sm-1">
                                                <a
                                                    class="btn btn-success btn-block py-2"
                                                    :class="{ disabled: !canPaySelectedLines }"
                                                    :href="paymentLink"
                                                    :aria-disabled="!canPaySelectedLines"
                                                    @click="goToPayment"
                                                ><strong>{{ $t("Betal nå") }}</strong></a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <NotInvoicedLines
                            :sales="notInvoicedLines"
                            :selectable="true"
                            :selected-sale-ids="selectedNotInvoicedLineIds"
                            @toggle-selection="toggleNotInvoicedLine"
                        ></NotInvoicedLines>
                    </div>
                </div>
            </div>
            <div v-if="!invoices.length && !notInvoicedLines.length">
                <div class="alert alert-info">
                    Du har inga obetalda köp
                </div>
            </div>
            <div v-if="paymentMethod">
                <div class="hr-wrapper">
                    <hr>
                </div>
                <DepositCard :payment-method="paymentMethod"></DepositCard>
            </div>

            <div class="d-flex flex-column gap-3">
                <div class="hr-wrapper">
                    <hr>
                </div>
                <div class="card">
                    <div class="row cursor-pointer" @click="toggleCollapseSaleList">
                        <div class="col-2">
                            <div class="card-body text-elev-blue">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="card-icon animate-300 align-middle"
                                     :class="{'fa-smooth-rotate-90' : accountExpanded}"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                </svg>

                            </div>
                        </div>
                        <div class="col-10">
                            <div class="card-body">
                                <h5 class="card-title text-xs">{{ $t('Klikk for å ekspandere') }}</h5>
                                <p class="text-2xl mb-0"><strong>{{ $t('Kontodetaljer') }}</strong></p>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 animate-300" :class="{ 'hidden-div': !accountExpanded, 'toggled-div': accountExpanding, 'd-block': !accountExpanding && accountExpanded }">
                            <SaleList></SaleList>
                        </div>
                    </div>
                </div>
                <div class="row" v-if="accountExpanded">
                    <AccountSummary></AccountSummary>
                </div>

                <div class="card">
                    <div class="row cursor-pointer" @click="toggleCollapseInvoiceList">
                        <div class="col-2">
                            <div class="card-body text-elev-blue">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="card-icon animate-300 align-middle"
                                     :class="{'fa-smooth-rotate-90' : invoicesExpanded}"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                </svg>

                            </div>
                        </div>
                        <div class="col-10">
                            <div class="card-body">
                                <h5 class="card-title text-xs">{{ $t('Klikk for å ekspandere') }}</h5>
                                <p class="text-2xl mb-0"><strong>{{ $t('Fakturahistorikk') }}</strong></p>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 animate-300" :class="{ 'hidden-div': !invoicesExpanded, 'toggled-div': invoicesExpanding, 'd-block': !invoicesExpanding && invoicesExpanded }">
                            <InvoiceHistory :isApp="isApp" :hashedKey="hashedKey"></InvoiceHistory>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Store from '../../state/payableInvoiceStore';
import AccountStore from '../../state/accountStore';
import { toMoney } from '../../lib/format';
import PayableInvoice from './PayableInvoice';
import NotInvoicedLines from './NotInvoicedLines';
import SaleList from '../account/SaleList';
import AccountSummary from '../account/Summary';
import InvoiceHistory from '../account/InvoiceHistory';
import DepositCard from './DepositCard';
import PositiveBalanceCard from "./PositiveBalanceCard.vue";

const props = defineProps(['isApp', 'hashedKey'])

const accountExpanded = ref(false);
const accountExpanding = ref(false);
const invoicesExpanded = ref(false);
const invoicesExpanding = ref(false);
const isApp = ref(props.isApp);
const hashedKey = ref(props.hashedKey ?? '');

onMounted(() => {
    Store.init();
    AccountStore.init();
});

const toggleCollapseSaleList = () => {
    accountExpanded.value = !accountExpanded.value;
    accountExpanding.value = true;
    setTimeout(() => {
        accountExpanding.value = false;
    }, 300);
};

const toggleCollapseInvoiceList = () => {
    invoicesExpanded.value = !invoicesExpanded.value;
    invoicesExpanding.value = true;
    setTimeout(() => {
        invoicesExpanding.value = false;
    }, 300);
};

const isLoading = computed(() => Store.isLoading());
const hasError = computed(() => Store.hasError());
const error = computed(() => Store.getError());
const invoices = computed(() => Store.getInvoices());
const notInvoicedLines = computed(() => Store.getNotInvoicedLines());
const notInvoicedAmountFormatted = computed(() => toMoney(Store.getSelectedNotInvoicedAmount()));
const selectedNotInvoicedLineIds = computed(() => Store.getSelectedNotInvoicedLineIds());
const showPaymentButton = computed(() => notInvoicedLines.value.length > 0 && paymentMethod.value);
const hasPositiveBalance = computed(() => !Store.hasNegativeBalance());
const totalBalanceAmount = computed(() => Store.getTotalBalanceAmount());
const totalBalanceAmountFormatted = computed(() => Store.getTotalBalanceAmountFormatted());
const paymentMethod = computed(() => {
    switch (true) {
        case Store.hasVipps():
            return 'vipps';
    }
    return null;
});
const canPaySelectedLines = computed(() => selectedNotInvoicedLineIds.value.length > 0);
const paymentLink = computed(() => {
    const saleIds = selectedNotInvoicedLineIds.value.join(',');
    return '/payment?method=' + paymentMethod.value + '&sales=1&sale_ids=' + encodeURIComponent(saleIds);
});

const toggleNotInvoicedLine = (saleId) => {
    Store.toggleNotInvoicedLine(saleId);
};

const goToPayment = (event) => {
    if (!canPaySelectedLines.value) {
        event.preventDefault();
    }
};
</script>